import React, { useEffect } from "react";
import TargetCard from "./TargetCard";
import FloorCard from "./FloorCard";
import { CardChart, CardTip } from "./Card";
import {
  BarStackChart,
  mockData as mockDataBar,
} from "../../../components/charts/barsStack";
import { P } from "../../../components/Common/Typo/typo";
import logo1 from "../../../assets/carbonfootprint/logo1.svg";
import logo2 from "../../../assets/carbonfootprint/logo2.svg";
import logo3 from "../../../assets/carbonfootprint/logo3.svg";
import logo4 from "../../../assets/carbonfootprint/logo4.svg";
import logo5 from "../../../assets/carbonfootprint/logo5.svg";
import logo6 from "../../../assets/carbonfootprint/logo6.svg";
import logo7 from "../../../assets/carbonfootprint/logo7.svg";
import logo8 from "../../../assets/carbonfootprint/logo8.svg";
import logo9 from "../../../assets/carbonfootprint/logo9.svg";
import logo10 from "../../../assets/carbonfootprint/logo10.svg";
import Trees from "../../../assets/trees.svg";
import GdBg from "../../../assets/gd-bg2.svg";
import useSWR from "swr";
import dayjs from "dayjs";
import useMonthlyCarbonFootprint from "../../../hooks/useMonthlyCarbonFootprint";
import BdBg from "../../../assets/bd-bg.svg";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";

const barColor: Record<string, string> = {
  energy: "#F5A623",
  garbage: "#00A4C3",
};

const carbonFootprintRename = {
  energy: "พลังงาน",
  garbage: "ขยะมูลฝอย",
};

const CarbonFootprint = () => {
  const queryParams = new URLSearchParams(location.search);
  const month = queryParams.get("month");
  const date = month
    ? dayjs(`${month.split("-")[1]}-${month.split("-")[0]}-01`)
    : dayjs();
  const [floorCardData, setFloorCardData] = React.useState<any>([]);
  const [iaqCardData, setIaqCardData] = React.useState([]);
  const [meterInterval, setMeterInterval] = React.useState<any>([]);
  const [targetCardData, setTargetCardData] = React.useState<any>([]);

  const { data: floorData } = useSWR([
    "v1/thaihealth/floor_data/",
    { month: date.format("MM-YYYY"), data_type: "carbon" },
  ]);
  const { data: monthlyCarbonData } = useMonthlyCarbonFootprint(12);
  const { data: iaqData } = useSWR(["/v1/thaihealth/air_quality/dashboard/"]);
  const { data: co2Data } = useSWR([
    "/v1/thaihealth/dashboard_data/co2_emission/",
    { month: date.format("MM-YYYY") },
  ]);
  const { data: targetData } = useSWR([
    "v1/thaihealth/target/",
    { page: "carbon", date: date.format("YYYY-MM-DD") },
  ]);

  useEffect(() => {
    const data: any = [
      {
        title: "คาร์บอนรวมทั้งอาคาร",
        value: targetData?.filter((data: any) => data.order === 1)[0].details
          .current_value,
        unit: targetData?.filter((data: any) => data.order === 1)[0].details
          .unit,
        percent: targetData?.filter((data: any) => data.order === 1)[0].percent,
        icon: "building",
        bgColor: "#6DAF5266",
        borderColor: "#6DB64F",
      },
      {
        title: "คาร์บอนจากพลังงาน",
        value: targetData?.filter((data: any) => data.order === 2)[0].details
          .current_value,
        unit: targetData?.filter((data: any) => data.order === 2)[0].details
          .unit,
        percent: targetData?.filter((data: any) => data.order === 2)[0].percent,
        icon: "thunder",
        bgColor: "#F5D9AA",
        borderColor: "#F5A623",
      },
      {
        title: "คาร์บอนจากขยะ",
        value: targetData?.filter((data: any) => data.order === 3)[0].details
          .current_value,
        unit: targetData?.filter((data: any) => data.order === 3)[0].details
          .unit,
        percent: targetData?.filter((data: any) => data.order === 3)[0].percent,
        icon: "trash",
        bgColor: "#9DD9E3",
        borderColor: "#00A4C3",
      },
    ];
    setTargetCardData(data);
  }, [targetData]);

  useEffect(() => {
    const data: any = [
      {
        title: "Roof",
        unit: "kgCO₂e",
        value: floorData?.filter((data: any) => data.name === "Roof")[0].value,
        top: 10,
        left: 80,
      },
      {
        title: "ชั้น 6",
        unit: "kgCO₂e",
        value: floorData?.filter((data: any) => data.name === "6")[0].value,
        top: 20,
        left: 5,
      },
      {
        title: "ชั้น 5",
        unit: "kgCO₂e",
        value: floorData?.filter((data: any) => data.name === "5")[0].value,
        top: 30,
        left: 80,
      },
      {
        title: "ชั้น 4",
        unit: "kgCO₂e",
        value: floorData?.filter((data: any) => data.name === "4")[0].value,
        top: 40,
        left: 5,
      },
      {
        title: "ชั้น 3",
        unit: "kgCO₂e",
        value: floorData?.filter((data: any) => data.name === "3")[0].value,
        top: 48,
        left: 80,
      },
      {
        title: "ชั้น 2",
        unit: "kgCO₂e",
        value: floorData?.filter((data: any) => data.name === "2")[0].value,
        top: 60,
        left: 5,
      },
      {
        title: "ชั้น 1",
        unit: "kgCO₂e",
        value: floorData?.filter((data: any) => data.name === "1")[0].value,
        top: 68,
        left: 80,
      },
      {
        title: "ชั้น B2",
        unit: "kgCO₂e",
        value: floorData?.filter((data: any) => data.name === "B2")[0].value,
        top: 80,
        left: 5,
      },
      {
        title: "ชั้น B1",
        unit: "kgCO₂e",
        value: floorData?.filter((data: any) => data.name === "B1")[0].value,
        top: 85,
        left: 80,
      },
    ];
    setFloorCardData(data);
  }, [floorData]);

  useEffect(() => {
    const data: any = [
      {
        title: "PM 2.5",
        value: iaqData?.pm25,
        unit: "µg/m³",
        status: iaqData?.pm25_status,
        key: "pm25",
      },
      {
        title: "CO₂",
        value: iaqData?.co2,
        unit: "ppm",
        status: iaqData?.co2_status,
        key: "co2",
      },
      {
        title: "PM 10",
        value: iaqData?.pm10,
        unit: "µg/m³",
        status: iaqData?.pm10_status,
        key: "pm10",
      },
    ];
    setIaqCardData(data);
    setMeterInterval(iaqData?.ranges);
  }, [iaqData]);

  const mockData3 = [
    {
      title: "สำหรับผู้เยี่ยมชมอาคาร",
      item: [
        { icon: logo1, value: "เลือกการเดินทางที่เป็นมิตรกับสิ่งแวดล้อม" },
        {
          icon: logo2,
          value: "คัดแยกขยะอย่างถูกวิธี เพื่อการรีไซเคิลที่มีคุณภาพ",
        },
        {
          icon: logo3,
          value: "ใช้บันไดแทนลิฟต์ สำหรับการขึ้นลงที่ไม่ไกลเกินไป",
        },
        { icon: logo4, value: "ปิดไฟและอุปกรณ์ไฟฟ้า เมื่อไม่ได้ใช้งาน" },
      ],
      color: "#FBDFB266",
      titleColor: "#ED9A0D",
    },
    {
      title: "สำหรับพนักงานในองค์กร",
      item: [
        {
          icon: logo5,
          value: "ใช้อุปกรณ์ที่มีประสิทธิภาพสูงในการประหยัดพลังงาน",
        },
        {
          icon: logo6,
          value: "ส่งเสริมการทำงานทางไกล เพื่อลดมลพิษจากการเดินทาง",
        },
        {
          icon: logo7,
          value: "ลดการใช้กระดาษเพื่อประหยัดทรัพยากร",
        },
        { icon: logo8, value: "จัดตั้งระบบการคัดแยกขยะที่ใช้งานได้จริง" },
        {
          icon: logo9,
          value: "เลือกใช้สินค้าที่ไม่ทำลายสิ่งแวดล้อมเมื่อเป็นไปได",
        },
        {
          icon: logo10,
          value: "ตรวจสอบและลดการปล่อยก๊าซเรือนกระจกอย่างสม่ำเสมอ",
        },
      ],
      color: "#E1FFD5",
      titleColor: "#51D81A",
    },
  ];
  const listData = [
    "สำหรับพลังงาน: 0.4999 kgCO₂e ต่อ kWh",
    "สำหรับขยะมูลฝอยทั่วไป:  2.3200 kgCO₂e ต่อ kg",
  ];
  return (
    <div
      className="h-full w-full relative"
      style={{
        background:
          // "radial-gradient(326.84% 138.02% at 0% 0%, #F9F9F9 0%, #ECFAFF 33%, #F4FFF0 66.5%, #FEF7EE 100%)",
          // "#ccf2ff",
          "#DCFAFF",
      }}
    >
      <div className="absolute top-0 left-0 px-8 py-4 w-full h-full">
        <div className="grid grid-cols-12 gap-2 h-full">
          <div className="col-span-3 h-full">
            <TargetCard
              targetData={targetData}
              type="carbon"
              targetCardData={targetCardData}
              iaqCardData={iaqCardData}
              meterInterval={meterInterval}
            />
          </div>
          <div className="col-span-6">
            <div className="flex flex-col gap-2 h-full">
              <div className="h-[60%]">
                <FloorCard
                  card_data={floorCardData}
                  title="คาร์บอนฟุตพรินต์รายชั้น (Carbon Footprint)"
                  date={date}
                />
              </div>
              <div className="h-[40%] border border-[#EDEFF9] bg-white rounded-md p-[10px]">
                <div className="flex items-center justify-between">
                  <P fontSize="30px" fontWeight="700" color="#1D1D1C">
                    เปรียบเทียบคาร์บอนฟุตพรินต์รายเดือน
                  </P>
                  <div className="px-[8px] rounded-md border border-[#DADADA] bg-[#EFEFEF]">
                    <P fontSize="20px" fontWeight="700" color="#6F6F6E">
                      Unit: กิโลกรัมเทียบเท่าคาร์บอนไดออกไซด์, kgCO₂e
                    </P>
                  </div>
                </div>
                {monthlyCarbonData && (
                  <BarStackChart
                    // map data to data with color
                    data={Object.keys(monthlyCarbonData).reduce(
                      (acc, key) => ({
                        ...acc,
                        [carbonFootprintRename[
                          key as keyof typeof carbonFootprintRename
                        ] || key]: {
                          ...monthlyCarbonData[
                            key as keyof typeof monthlyCarbonData
                          ],
                          color: barColor[key],
                        },
                      }),
                      {}
                    )}
                    title=""
                    yAxisName=""
                    height="90%"
                  />
                )}
              </div>
            </div>
          </div>
          <div className="col-span-3">
            <div className="flex flex-col gap-2 h-full">
              <div className="p-[10px] h-[33%] border border-[#EDEFF9] bg-white rounded-md flex flex-col gap-2">
                <p className="text-2xl font-bold">ข้อมูลการลดการปล่อยคาร์บอน</p>
                <div className="flex gap-2">
                  <div
                    className={`bg-white rounded-[10px] border border-[#d6d7df] shadow-badgelogout w-full col-start-1 col-end-2 flex justify-between items-center z-1 relative`}
                  >
                    <img
                      src={
                        co2Data?.co2_emission_save?.percent > 100 ? BdBg : GdBg
                      }
                      alt="Icon"
                      className={`absolute top-0 left-0 w-full h-full object-cover rounded-[10px] z-0 opacity-80`}
                    />

                    <div className="p-2">
                      <P fontSize="18px" color="#1D1D1C">
                        อัตราการปล่อยคาร์บอน
                      </P>
                      <div className="flex items-center gap-1">
                        <p className="text-[20px] text-[#788796] mt-1">
                          {co2Data?.co2_emission_save?.percent > 100
                            ? "เพิ่มขึ้น"
                            : "ลดลง"}
                        </p>
                        <p className="text-[30px] text-[#212529] font-bold z-10">
                          {co2Data?.co2_emission_save?.percent
                            ? Math.abs(
                                co2Data?.co2_emission_save?.percent
                              )?.toLocaleString(undefined, {
                                maximumFractionDigits: 2,
                              })
                            : 0}
                        </p>
                        <p className="text-[20px] text-[#788796] mt-1">%</p>
                        {co2Data?.co2_emission_save?.percent > 100 ? (
                          <FaArrowUp className="fill-[#EF4337]" />
                        ) : (
                          <FaArrowDown className="fill-[#43A452] h-[32px]" />
                        )}
                      </div>

                      <p className="text-[12px] text-[#788796] leading-[12px] -mt-2">
                        {co2Data?.co2_emission_save?.percent > 100
                          ? "เพิ่มขึ้น"
                          : "ลดลง"}{" "}
                        {co2Data?.co2_emission_save?.value?.toLocaleString(
                          undefined,
                          {
                            maximumFractionDigits: 2,
                          }
                        ) ?? 0}{" "}
                        kgCO₂e
                      </p>

                      <P fontSize="16px" color="#212529">
                        เปรียบเทียบกับเดือนที่ผ่านมา
                      </P>
                    </div>
                  </div>
                  <div className=" bg-white rounded-[10px] border border-[#d6d7df] shadow-badgelogout w-full flex justify-between items-center relative overflow-hidden">
                    <div className="p-2 flex flex-col justify-between">
                      <P fontSize="20px" color="#1D1D1C">
                        เทียบเท่ากับการปลูกต้นไม้ทดแทน
                      </P>
                      <div className="flex items-center gap-1">
                        <p className="text-[20px] text-[#788796] mt-1">
                          {co2Data?.co2_emission_save?.percent > 100
                            ? "เพิ่มขึ้น"
                            : "ลดลง"}
                        </p>
                        <P fontSize="30px" color="#212529" fontWeight="700">
                          {Math.abs(
                            co2Data?.eq_trees_planted?.value
                          )?.toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                          }) ?? 0}
                        </P>
                        <p className="text-[20px] text-[#788796] mt-1">ต้น</p>
                        {co2Data?.co2_emission_save?.percent > 100 ? (
                          <FaArrowUp className="fill-[#EF4337]" />
                        ) : (
                          <FaArrowDown className="fill-[#43A452] h-[32px]" />
                        )}
                      </div>
                      <div className="text-[#212529] text-[14px] leading-[15px]">
                        <p>เปรียบเทียบกับเดือนที่ผ่านมา</p>
                        <p>ใน 1 ปี ต้นไม้สามารถดูดซับคาร์บอน</p>
                        <p>
                          ได้ {co2Data?.eq_trees_planted?.factor} kgCO₂e / ต้น
                        </p>
                      </div>
                    </div>
                    <div
                      className={`absolute bottom-0 -right-4 bg-no-repeat bg-cover bg-center`}
                    >
                      <img
                        src={Trees}
                        alt="Icon"
                        className={`w-full h-full object-cover`}
                      />
                    </div>
                  </div>
                </div>
                <CardTip title="ตัวคูณการปล่อย CO₂" listData={listData}>
                  <P fontSize="16px" color="#1E9282">
                    *อ้างอิง: องค์การบริหารจัดการก๊าซเรือนกระจก มี.ค. 67
                  </P>
                </CardTip>
              </div>
              <div className="h-[70%]">
                <div className="p-[10px] rounded-xl border border-[#EDEFF9] bg-white shadow-cardborder h-full">
                  <P fontSize="24px" fontWeight="700" color="#1D1D1C">
                    วิธีการลดการปล่อยคาร์บอน
                  </P>
                  {mockData3.map((data: any) => (
                    <div className="my-2">
                      <div
                        className="px-[6px] py-[2px] rounded-[3px] opacity-70"
                        style={{
                          background: data.titleColor,
                        }}
                      >
                        <P fontSize="24px" color="#FFF" className="text-center">
                          {data.title}
                        </P>
                      </div>
                      <div className="grid grid-cols-2 gap-[6px] mt-[6px]">
                        {data.item.map((items: any) => (
                          <div
                            className="p-[6px] rounded-lg border border-[#EDEFF9] grid grid-cols-2 items-center justify-center"
                            style={{ background: data.color }}
                          >
                            <div className="flex items-center justify-center">
                              <img src={items.icon} width={60} />
                            </div>

                            <P fontSize="16px" color="#063E40">
                              {items.value}
                            </P>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarbonFootprint;
